'use client';

import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';

export interface ContactQuota {
  used: number;
  limit: number;
  remaining: number;
}

/**
 * One quota figure shared by every "View contact" button on the page. A match list renders
 * dozens of them, so the fetch happens once and updates are pushed to all subscribers —
 * viewing a contact or paying anywhere refreshes the count everywhere.
 */
let cache: ContactQuota | null = null;
let inflight: Promise<void> | null = null;
const listeners = new Set<(quota: ContactQuota | null) => void>();

const emit = () => listeners.forEach((listener) => listener(cache));

/** Push a known-fresh figure (the contact-view API returns one on every call). */
export function setContactQuota(next: ContactQuota | null) {
  cache = next;
  emit();
}

function load(force = false): Promise<void> {
  if (!force && cache) return Promise.resolve();
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const res = await fetch('/api/user/quota', { credentials: 'include' });
      if (!res.ok) {
        cache = null;
        return;
      }
      const json = await res.json();
      const contacts = json?.data?.contact_numbers;
      cache = contacts
        ? {
            used: Number(contacts.used) || 0,
            limit: Number(contacts.limit) || 0,
            remaining: Math.max(0, (Number(contacts.limit) || 0) - (Number(contacts.used) || 0)),
          }
        : null;
    } catch {
      cache = null;
    } finally {
      inflight = null;
      emit();
    }
  })();

  return inflight;
}

export const refreshContactQuota = () => load(true);

export function useContactQuota(): ContactQuota | null {
  const { user } = useAuth();
  const [quota, setQuota] = useState<ContactQuota | null>(cache);

  useEffect(() => {
    if (!user) return;
    listeners.add(setQuota);
    load();
    return () => {
      listeners.delete(setQuota);
    };
  }, [user]);

  return user ? quota : null;
}
